import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const ax  = axios.create({ baseURL: API, withCredentials: true });

const CLIENT_STATUS_LABELS = {
  prospect:    { label: 'Prospect',      color: '#60a5fa' },
  devis:       { label: 'Devis envoyé',  color: '#fbbf24' },
  active:      { label: 'En production', color: '#C8FF00' },
  completed:   { label: 'Livré',         color: '#4ade80' },
  maintenance: { label: 'Maintenance',   color: '#a78bfa' },
  inactive:    { label: 'Inactif',       color: '#444'    },
};

function KPI({ label, value, sub, color = '#C8FF00', icon }) {
  return (
    <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px', flex:1, minWidth:160 }}>
      <div style={{ fontSize:'1.4rem', marginBottom:6 }}>{icon}</div>
      <div style={{ fontSize:'2rem', fontWeight:900, color, letterSpacing:'-0.04em', lineHeight:1 }}>{value}</div>
      <div style={{ color:'#555', fontSize:'0.72rem', fontWeight:700, marginTop:7, textTransform:'uppercase', letterSpacing:'0.07em' }}>{label}</div>
      {sub && <div style={{ color:'#333', fontSize:'0.69rem', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function Bar({ label, count, total, color, extra }) {
  const pct = total ? (count / total) * 100 : 0;
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
        <span style={{ color:'#666', fontSize:'0.78rem' }}>{label}</span>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {extra && <span style={{ color:'#333', fontSize:'0.7rem' }}>{extra}</span>}
          <span style={{ color, fontWeight:800, fontSize:'0.8rem', minWidth:16, textAlign:'right' }}>{count}</span>
        </div>
      </div>
      <div style={{ height:5, background:'rgba(255,255,255,0.05)', borderRadius:99, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:99, transition:'width 0.7s ease' }} />
      </div>
    </div>
  );
}

const scoreStyle = s => {
  if (s <= 30) return { c:'#f87171', label:'CHAUD' };
  if (s <= 60) return { c:'#fbbf24', label:'TIÈDE' };
  return             { c:'#4ade80', label:'ÉTABLI' };
};

export default function AnalyticsSection() {
  const [clients,  setClients]  = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [leads,    setLeads]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [c, inv, p, w, l] = await Promise.all([
          getDocs(collection(firestore, 'clients')),
          getDocs(collection(firestore, 'invoices')),
          getDocs(collection(firestore, 'projects')),
          getDocs(collection(firestore, 'websites')),
          ax.get('/api/leads').catch(() => ({ data: [] })),
        ]);
        setClients(c.docs.map(d => ({ id: d.id, ...d.data() })));
        setInvoices(inv.docs.map(d => ({ id: d.id, ...d.data() })));
        setProjects(p.docs.map(d => ({ id: d.id, ...d.data() })));
        setWebsites(w.docs.map(d => ({ id: d.id, ...d.data() })));
        setLeads(l.data || []);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const prefs = (() => {
    try { return JSON.parse(localStorage.getItem('ucreative_prefs') || '{}'); } catch { return {}; }
  })();

  const pipelineStages = prefs.pipelineStages || [
    { value: 'à_contacter', label: '📋 À contacter', color: '#888' },
    { value: 'contacté',    label: '📬 Contacté',    color: '#63b3ed' },
    { value: 'proposition', label: '💼 Proposition',  color: '#fbbf24' },
    { value: 'signé',       label: '✅ Signé',         color: '#4ade80' },
    { value: 'refusé',      label: '❌ Refusé',        color: '#f87171' },
  ];

  // ── Calculs ───────────────────────────────────────────────────────────────
  const paidInvoices    = invoices.filter(i => i.status === 'payé');
  const pendingInvoices = invoices.filter(i => i.status === 'en_attente');
  const lateInvoices    = invoices.filter(i => i.status === 'en_retard');
  const totalRevenue    = paidInvoices.reduce((s, i) => s + Number(i.amount || 0), 0);
  const pendingRevenue  = pendingInvoices.reduce((s, i) => s + Number(i.amount || 0), 0);

  const now = new Date();
  const months = Array.from({ length: 6 }, (_, idx) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + idx, 1);
    return { key:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label:d.toLocaleString('fr-CA',{month:'short'}), total:0 };
  });
  paidInvoices.forEach(inv => {
    const raw  = inv.createdAt;
    const date = raw?.toDate ? raw.toDate() : raw ? new Date(raw) : null;
    if (!date) return;
    const key = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}`;
    const m = months.find(m => m.key === key);
    if (m) m.total += Number(inv.amount || 0);
  });
  const maxMonth = Math.max(...months.map(m => m.total), 1);

  const clientsByStatus = {};
  clients.forEach(c => { clientsByStatus[c.status] = (clientsByStatus[c.status] || 0) + 1; });

  // Pipeline depuis Firestore leads
  const leadsByStage = {};
  leads.forEach(l => {
    const s = l.pipelineStatus;
    if (s) leadsByStage[s] = (leadsByStage[s] || 0) + 1;
  });
  const totalLeads   = leads.length;
  const signedLeads  = leadsByStage['signé'] || 0;
  const hotLeads     = leads.filter(l => l.score <= 30).length;
  const warmLeads    = leads.filter(l => l.score > 30 && l.score <= 60).length;
  const noWebLeads   = leads.filter(l => !l.website).length;
  const activeSites  = websites.filter(w => w.status === 'en_ligne').length;
  const activeProjects = projects.filter(p => p.status === 'active').length;

  const fmt = n => n >= 1000 ? `${(n/1000).toFixed(1)}k$` : `${n}$`;

  if (loading) return (
    <div style={{ padding:32, display:'flex', alignItems:'center', justifyContent:'center', minHeight:300 }}>
      <div style={{ color:'#333', fontSize:'0.88rem' }}>Chargement...</div>
    </div>
  );

  return (
    <div style={{ padding:32, maxWidth:1000 }}>

      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontSize:'0.63rem', color:'#333', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:6 }}>Ucreative</div>
        <h1 style={{ fontSize:'2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em', margin:'0 0 6px' }}>Analytics</h1>
        <p style={{ color:'#333', fontSize:'0.83rem', margin:0 }}>Vue d'ensemble de l'activité de l'agence</p>
      </div>

      {/* KPIs */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <KPI icon="💰" label="Revenus encaissés" value={fmt(totalRevenue)}
          sub={pendingRevenue > 0 ? `+ ${fmt(pendingRevenue)} en attente` : invoices.length === 0 ? 'Aucune facture' : 'Tout encaissé'}
          color="#C8FF00" />
        <KPI icon="👥" label="Clients" value={clients.length}
          sub={`${clients.filter(c=>c.status==='active').length} en production`}
          color="#60a5fa" />
        <KPI icon="🎯" label="Leads prospectés" value={totalLeads}
          sub={`${hotLeads} chauds · ${signedLeads} signés`}
          color="#f87171" />
        <KPI icon="🌐" label="Sites en ligne" value={activeSites}
          sub={`${websites.length} sites gérés`}
          color="#4ade80" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>

        {/* Revenus mensuels */}
        <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
          <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:16 }}>Revenus mensuels (6 mois)</div>
          {totalRevenue === 0 ? (
            <div style={{ color:'#222', fontSize:'0.82rem', paddingTop:8 }}>Aucune facture payée encore</div>
          ) : (
            <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:100 }}>
              {months.map(m => {
                const pct = (m.total / maxMonth) * 100;
                const isCur = m.key === `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
                return (
                  <div key={m.key} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4, height:'100%', justifyContent:'flex-end' }}>
                    <div style={{ fontSize:'0.58rem', color: m.total > 0 ? '#C8FF00' : '#2a2a2a', fontWeight:700, marginBottom:2 }}>
                      {m.total > 0 ? fmt(m.total) : ''}
                    </div>
                    <div style={{ width:'100%', borderRadius:'4px 4px 0 0', height:`${Math.max(pct,2)}%`,
                      background: isCur ? '#C8FF00' : m.total > 0 ? 'rgba(200,255,0,0.35)' : 'rgba(255,255,255,0.04)' }} />
                    <div style={{ fontSize:'0.62rem', color:'#444', textTransform:'capitalize', marginTop:4 }}>{m.label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pipeline leads */}
        <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
          <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4 }}>Pipeline prospection</div>
          <div style={{ color:'#2a2a2a', fontSize:'0.72rem', marginBottom:14 }}>{totalLeads} lead{totalLeads !== 1 ? 's' : ''} suivi{totalLeads !== 1 ? 's' : ''}</div>
          {totalLeads === 0 ? (
            <div style={{ color:'#222', fontSize:'0.82rem' }}>Allez dans Prospection pour trouver vos premiers leads</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {pipelineStages.map(st => (
                <Bar key={st.value} label={st.label} count={leadsByStage[st.value]||0} total={totalLeads} color={st.color} />
              ))}
            </div>
          )}
        </div>

        {/* Qualité des leads */}
        <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
          <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4 }}>Qualité des leads</div>
          <div style={{ color:'#2a2a2a', fontSize:'0.72rem', marginBottom:14 }}>Répartition par score de présence web</div>
          {totalLeads === 0 ? (
            <div style={{ color:'#222', fontSize:'0.82rem' }}>Aucun lead encore</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <Bar label="🔴 Chauds (0–30)" count={hotLeads} total={totalLeads} color="#f87171"
                extra={hotLeads > 0 ? 'Priorité max' : ''} />
              <Bar label="🟡 Tièdes (31–60)" count={warmLeads} total={totalLeads} color="#fbbf24" />
              <Bar label="🟢 Établis (61–100)" count={totalLeads-hotLeads-warmLeads} total={totalLeads} color="#4ade80" />
              <div style={{ marginTop:6, paddingTop:12, borderTop:'1px solid rgba(255,255,255,0.05)', display:'flex', gap:20, flexWrap:'wrap' }}>
                <div>
                  <div style={{ color:'#f87171', fontWeight:800, fontSize:'0.9rem' }}>{noWebLeads}</div>
                  <div style={{ color:'#333', fontSize:'0.68rem' }}>sans site web</div>
                </div>
                <div>
                  <div style={{ color:'#C8FF00', fontWeight:800, fontSize:'0.9rem' }}>{signedLeads}</div>
                  <div style={{ color:'#333', fontSize:'0.68rem' }}>signés</div>
                </div>
                <div>
                  <div style={{ color:'#4ade80', fontWeight:800, fontSize:'0.9rem' }}>
                    {totalLeads > 0 ? Math.round((signedLeads / totalLeads) * 100) : 0}%
                  </div>
                  <div style={{ color:'#333', fontSize:'0.68rem' }}>taux de conversion</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Clients par statut */}
        <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
          <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4 }}>Clients par statut</div>
          <div style={{ color:'#2a2a2a', fontSize:'0.72rem', marginBottom:14 }}>{clients.length} client{clients.length !== 1 ? 's' : ''} au total</div>
          {clients.length === 0 ? (
            <div style={{ color:'#222', fontSize:'0.82rem' }}>Aucun client enregistré</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {Object.entries(CLIENT_STATUS_LABELS).map(([key, { label, color }]) => {
                const count = clientsByStatus[key] || 0;
                if (!count) return null;
                return <Bar key={key} label={label} count={count} total={clients.length} color={color} />;
              })}
            </div>
          )}
        </div>

      </div>

      {/* Tableau leads récents */}
      {totalLeads > 0 && (
        <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, marginBottom:14, overflow:'hidden' }}>
          <div style={{ padding:'16px 22px', borderBottom:'1px solid rgba(255,255,255,0.05)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' }}>Leads récents</div>
            <span style={{ color:'#2a2a2a', fontSize:'0.72rem' }}>{totalLeads} total</span>
          </div>
          <div>
            {leads.slice(0, 8).map((lead, i) => {
              const sc = scoreStyle(lead.score || 0);
              const st = lead.pipelineStatus;
              const stage = pipelineStages.find(p => p.value === st);
              return (
                <div key={lead.id || i} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 22px',
                  borderBottom: i < Math.min(leads.length, 8) - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>

                  {/* Score */}
                  <div style={{ width:36, height:36, borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column',
                    background:`conic-gradient(${sc.c} ${(lead.score||0)*3.6}deg, #1c1c1c ${(lead.score||0)*3.6}deg)` }}>
                    <div style={{ width:26, height:26, borderRadius:'50%', background:'#0d0d10', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <span style={{ fontSize:'0.6rem', fontWeight:900, color:sc.c }}>{lead.score||0}</span>
                    </div>
                  </div>

                  {/* Nom + adresse */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ color:'#EAEAEA', fontWeight:700, fontSize:'0.84rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {lead.name}
                    </div>
                    <div style={{ color:'#333', fontSize:'0.7rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {lead.address}
                    </div>
                  </div>

                  {/* Website */}
                  <div style={{ flexShrink:0 }}>
                    {lead.website
                      ? <span style={{ color:'#4ade80', fontSize:'0.7rem' }}>🔒 Site</span>
                      : <span style={{ color:'#f87171', fontSize:'0.7rem', fontWeight:700 }}>🌐 Sans site</span>
                    }
                  </div>

                  {/* Note */}
                  {lead.rating != null && (
                    <div style={{ flexShrink:0, color:'#555', fontSize:'0.72rem' }}>
                      ⭐ {lead.rating} <span style={{ color:'#2a2a2a' }}>({lead.reviewCount})</span>
                    </div>
                  )}

                  {/* Statut pipeline */}
                  {stage ? (
                    <span style={{ background:`${stage.color}15`, border:`1px solid ${stage.color}30`, color:stage.color,
                      fontSize:'0.65rem', fontWeight:700, padding:'2px 9px', borderRadius:5, flexShrink:0, whiteSpace:'nowrap' }}>
                      {stage.label}
                    </span>
                  ) : (
                    <span style={{ color:'#222', fontSize:'0.68rem', flexShrink:0 }}>Non classé</span>
                  )}
                </div>
              );
            })}
          </div>
          {leads.length > 8 && (
            <div style={{ padding:'10px 22px', borderTop:'1px solid rgba(255,255,255,0.04)', color:'#2a2a2a', fontSize:'0.72rem', textAlign:'center' }}>
              + {leads.length - 8} autres leads — allez dans Prospection pour tout voir
            </div>
          )}
        </div>
      )}

      {/* Projets actifs */}
      {activeProjects > 0 && (
        <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
          <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:16 }}>Projets en cours</div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {projects.filter(p => p.status === 'active').map(p => (
              <div key={p.id} style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:'#EAEAEA', fontSize:'0.84rem', fontWeight:700, marginBottom:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.name}</div>
                  {p.client && <div style={{ color:'#444', fontSize:'0.72rem' }}>{p.client}</div>}
                </div>
                <div style={{ flexShrink:0, width:140 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                    <span style={{ color:'#444', fontSize:'0.68rem' }}>Avancement</span>
                    <span style={{ color:'#C8FF00', fontSize:'0.68rem', fontWeight:700 }}>{p.progress||0}%</span>
                  </div>
                  <div style={{ height:4, background:'rgba(255,255,255,0.06)', borderRadius:99, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${p.progress||0}%`, background:'#C8FF00', borderRadius:99 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* État vide global */}
      {totalLeads === 0 && clients.length === 0 && invoices.length === 0 && (
        <div style={{ textAlign:'center', padding:'48px 20px', color:'#222' }}>
          <div style={{ fontSize:48, marginBottom:14 }}>📊</div>
          <div style={{ fontSize:'1rem', fontWeight:700, color:'#2a2a2a', marginBottom:6 }}>Aucune donnée encore</div>
          <div style={{ fontSize:'0.82rem', lineHeight:1.7 }}>
            Commencez par prospecter dans <strong style={{color:'#555'}}>Lead Finder</strong><br/>
            ou ajoutez des clients dans <strong style={{color:'#555'}}>Clients</strong>
          </div>
        </div>
      )}

    </div>
  );
}
